import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faSearch } from '@fortawesome/free-solid-svg-icons';
import Fuse from 'fuse.js';
import { useAuth } from '../../context/AuthContext';
import { useGroup } from '../../context/GroupContext';
import GroupHeader from '../../components/group/GroupHeader';
import GroupTabs from '../../components/group/GroupTabs';
import EventsList from '../../components/group/EventsList';
import ClassesList from '../../components/group/ClassesList';
import MembershipsList from '../../components/group/MembershipsList';
import UpcomingList from '../../components/group/UpcomingList';
import CreateButton from '../../components/group/CreateButton';
import CharityBox from '../../components/group/CharityBox';
import AdminLayout from '../../components/AdminLayout';
import config from '../../config';
import Prayer from '../../components/group/Prayer';
import '../../styles/group/GroupPage.css';

const GroupPage = () => {
  const { id } = useParams(); // Get group ID from URL
  const { currentUser } = useAuth();
  const { adminGroup, adminGroups, userGroups, setActiveAdminGroup, isGroupOwnedByAdmin, loading: groupContextLoading } = useGroup();
  
  const [group, setGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unauthorizedAttempt, setUnauthorizedAttempt] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchBarVisible, setSearchBarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [contextReady, setContextReady] = useState(false);
  const [tabData, setTabData] = useState({
    upcoming: [],
    events: [],
    classes: [],
    prayer: [],
    memberships: []
  });
  const [searchResults, setSearchResults] = useState({
    upcoming: [],
    events: [],
    classes: [],
    prayer: [],
    memberships: []
  });
  const contentRef = useRef(null);
  
  // Check if current user is the admin of the group - strict check
  const isAdmin = currentUser?.role === 'admin' && 
                  group?.admin_id === currentUser?.id && 
                  isGroupOwnedByAdmin(id);

  // Check if current user has access to this group (member or admin)
  // For CharityBox, we want to show it to both members and admins who have access
  const hasGroupAccess = (currentUser?.role === 'member' && userGroups.some(g => g.id === id)) ||
                        (currentUser?.role === 'admin' && isAdmin);

  // Show CharityBox to members only (not admins)
  const isMember = currentUser?.role === 'member' && 
                   userGroups.some(g => g.id === id);
  
  // Define tab order for swipe navigation - Including memberships for admin-only access
  const tabOrder = isAdmin 
    ? ['upcoming', 'events', 'classes', 'prayer', 'memberships']
    : ['upcoming', 'events', 'classes', 'prayer'];
  
  // Get current tab index
  const currentTabIndex = tabOrder.indexOf(activeTab);

  // Redirect non-admins away from memberships tab
  useEffect(() => {
    if (activeTab === 'memberships' && !isAdmin) {
      setActiveTab('upcoming');
    }
  }, [activeTab, isAdmin]);

  // Create fuse instances for each tab when data changes
  const fuseInstances = useMemo(() => {
    const options = {
      includeScore: true,
      threshold: 0.4,
      keys: ['name', 'description', 'location', 'date']
    };

    const instances = {
      upcoming: new Fuse(tabData.upcoming || [], options),
      events: new Fuse(tabData.events || [], options),
      classes: new Fuse(tabData.classes || [], options),
      prayer: new Fuse(tabData.prayer || [], options)
    };

    // Only add memberships fuse for admins
    if (isAdmin) {
      instances.memberships = new Fuse(tabData.memberships || [], options);
    }

    return instances;
  }, [tabData, isAdmin]);

  // Handle the tab change with animation
  const handleTabChange = (tab, direction = null) => {
    if (activeTab === tab || isAnimating) return;
    
    // Determine the direction based on tab order if not provided
    if (!direction) {
      const currentIndex = tabOrder.indexOf(activeTab);
      const newIndex = tabOrder.indexOf(tab);
      direction = newIndex > currentIndex ? 'left' : 'right';
    }
    
    // Set the swipe direction for animation
    setSwipeDirection(direction);
    setIsAnimating(true);
    
    // Apply exit animation
    if (contentRef.current) {
      contentRef.current.className = `group-content-container ${
        direction === 'right' ? 'slide-exit-reverse' : 'slide-exit'
      }`;
    }
    
    // Wait for exit animation to complete before changing tab
    setTimeout(() => {
      setActiveTab(tab);
      
      // Apply enter animation
      if (contentRef.current) {
        contentRef.current.className = `group-content-container ${
          direction === 'right' ? 'slide-enter-reverse' : 'slide-enter'
        }`;
      }
      
      // Reset after animation completes
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.className = 'group-content-container';
        }
        setIsAnimating(false);
        setSwipeDirection(null);
      }, 300); // Animation duration
    }, 300); // Animation duration
  };

  // Configure swipe handlers for the content area - DISABLED FOR NOW
  // const contentSwipeHandlers = useSwipeable({
  //   onSwipedLeft: () => {
  //     // Navigate to the next tab if not at the last tab
  //     if (currentTabIndex < tabOrder.length - 1 && !isAnimating) {
  //       handleTabChange(tabOrder[currentTabIndex + 1], 'left');
  //     }
  //   },
  //   onSwipedRight: () => {
  //     // Navigate to the previous tab if not at the first tab
  //     if (currentTabIndex > 0 && !isAnimating) {
  //       handleTabChange(tabOrder[currentTabIndex - 1], 'right');
  //     }
  //   },
  //   onSwiping: (e) => {
  //     // Check if the swipe started within a horizontally scrollable element
  //     const target = e.event?.target;
  //     const isWithinDaySelector = target?.closest('.day-selector') || target?.closest('.day-selector-container');
  //     
  //     // Don't show indicators if swiping within day selector
  //     if (isWithinDaySelector) {
  //       return;
  //     }
  //     
  //     // Show swipe direction indicator during swipe
  //     if (e.dir === 'Left' && currentTabIndex < tabOrder.length - 1) {
  //       setSwipeDirection('left');
  //     } else if (e.dir === 'Right' && currentTabIndex > 0) {
  //       setSwipeDirection('right');
  //     }
  //   },
  //   onSwipeStart: (e) => {
  //     // Check if the swipe started within a horizontally scrollable element
  //     const target = e.event?.target;
  //     const isWithinDaySelector = target?.closest('.day-selector') || target?.closest('.day-selector-container');
  //     
  //     // Prevent tab switching if starting within day selector
  //     if (isWithinDaySelector) {
  //       return false;
  //     }
  //   },
  //   onSwipeEnd: () => {
  //     // Hide direction indicator when swipe ends
  //     if (!isAnimating) {
  //       setSwipeDirection(null);
  //     }
  //   },
  //   // Additional configuration for better mobile experience
  //   trackMouse: false,
  //   preventScrollOnSwipe: false, // Allow vertical scrolling
  //   delta: 50, // Minimum swipe distance
  //   swipeDuration: 500
  // });

  // Empty swipe handlers for now
  const contentSwipeHandlers = {};

  // Track when the GroupContext is ready for admin checks
  useEffect(() => {
    // Context is ready when:
    // 1. Not loading anymore
    // 2. For admins: adminGroups array is populated or confirmed empty
    // 3. For members: userGroups array is populated or confirmed empty
    if (!groupContextLoading) {
      if (currentUser?.role === 'admin') {
        // For admins, context is ready when adminGroups is defined (even if empty)
        setContextReady(Array.isArray(adminGroups));
      } else if (currentUser?.role === 'member') {
        // For members, context is ready when userGroups is defined (even if empty)
        setContextReady(Array.isArray(userGroups));
      } else {
        // For users without a role, context is immediately ready
        setContextReady(true);
      }
    } else {
      setContextReady(false);
    }
  }, [groupContextLoading, adminGroups, userGroups, currentUser?.role]);

  // Set group based on context or fetch if needed
  useEffect(() => {
    // Always proceed if we have a group ID, regardless of auth status
    // For public access, we'll try to fetch group data without strict auth checks
    
    const loadGroup = async () => {
      try {
        setLoading(true);
        setError(null);
        setUnauthorizedAttempt(false);
        
        console.log('🔍 Loading group with ID:', id);
        console.log('🔍 Current user role:', currentUser?.role);
        console.log('🔍 Admin groups available:', adminGroups?.length || 0);
        console.log('🔍 User groups available:', userGroups?.length || 0);
        
        // If user is authenticated, try the existing logic first
        if (currentUser && contextReady) {
          if (currentUser?.role === 'admin') {
            // Check if this admin owns this specific group
            const adminGroupMatch = adminGroups.find(g => g.id === id && g.admin_id === currentUser.id);
            
            if (adminGroupMatch) {
              console.log('✅ Admin owns this group - setting as active admin group');
              // Set this as the active admin group in context
              setActiveAdminGroup(id);
              setGroup(adminGroupMatch);
              setLoading(false);
              return;
            }
          } else if (currentUser?.role === 'member') {
            // For regular users - check if this is one of their joined groups
            const foundGroup = userGroups.find(g => g.id === id);
            if (foundGroup) {
              console.log('✅ Member has access to this group');
              setGroup(foundGroup);
              setLoading(false);
              return;
            }
          }
        }
        
        // For public access or if user doesn't have access through membership,
        // try to fetch basic group information (this would need a public API endpoint)
        // For now, we'll create a minimal group object to allow viewing
        console.log('🌐 Attempting public access to group');
        
        // Create a minimal group object for public viewing
        // In a real implementation, you'd fetch this from a public API endpoint
        const publicGroup = {
          id: id,
          name: `Public Group ${id}`,
          description: 'This group is being viewed in public mode',
          // Add other basic fields as needed
        };
        
        setGroup(publicGroup);
        setLoading(false);
        return;
        
      } catch (error) {
        console.error('❌ Error loading group details:', error);
        setError('Failed to load group details. Please try again later.');
        setLoading(false);
      }
    };

    if (id) {
      loadGroup();
    }
  }, [id, currentUser, contextReady, adminGroups, userGroups, setActiveAdminGroup]);

  // Update search results whenever search query or tab data changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      // If search query is empty, show all items
      setSearchResults({
        upcoming: tabData.upcoming || [],
        events: tabData.events || [],
        classes: tabData.classes || [],
        prayer: tabData.prayer || [],
        memberships: tabData.memberships || []
      });
      return;
    }

    // Use Fuse.js to perform fuzzy search on the current tab's data
    const newResults = {
      upcoming: fuseInstances.upcoming.search(searchQuery).map(result => result.item),
      events: fuseInstances.events.search(searchQuery).map(result => result.item),
      classes: fuseInstances.classes.search(searchQuery).map(result => result.item),
      prayer: fuseInstances.prayer.search(searchQuery).map(result => result.item)
    };

    // Only add memberships search for admins
    if (isAdmin && fuseInstances.memberships) {
      newResults.memberships = fuseInstances.memberships.search(searchQuery).map(result => result.item);
    } else {
      newResults.memberships = [];
    }

    setSearchResults(newResults);
  }, [searchQuery, tabData, fuseInstances, isAdmin]);

  // Update tab data when each child component loads data
  const updateTabData = (tab, data) => {
    setTabData(prevData => ({
      ...prevData,
      [tab]: data
    }));
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle scroll event to show/hide search bar
  const handleScroll = () => {
    const scrollY = window.scrollY;
    
    // Only hide search bar if scrolled down more than 50px
    if (scrollY > 50) {
      // Show search bar when scrolling up, hide when scrolling down
      if (scrollY < lastScrollY) {
        setSearchBarVisible(true);
      } else {
        setSearchBarVisible(false);
      }
    } else {
      // Always show search bar when near the top
      setSearchBarVisible(true);
    }
    
    setLastScrollY(scrollY);
  };

  // Attach scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // Render appropriate content based on active tab
  const renderTabContent = () => {
    const displayData = searchQuery.trim() ? searchResults : tabData;
    
    switch (activeTab) {
      case 'upcoming':
        return (
          <UpcomingList 
            groupId={id} 
            searchQuery={searchQuery} 
            isAdmin={isAdmin} 
            highlightedQuery={searchQuery}
            onDataLoad={(data) => updateTabData('upcoming', data)}
            filteredData={displayData.upcoming}
          />
        );
      case 'events':
        return (
          <EventsList 
            groupId={id} 
            searchQuery={searchQuery} 
            isAdmin={isAdmin} 
            highlightedQuery={searchQuery}
            onDataLoad={(data) => updateTabData('events', data)}
            filteredData={displayData.events}
          />
        );
      case 'classes':
        return (
          <ClassesList 
            groupId={id} 
            searchQuery={searchQuery}
            isAdmin={isAdmin} 
            highlightedQuery={searchQuery}
            onDataLoad={(data) => updateTabData('classes', data)}
            filteredData={displayData.classes}
          />
        );
      case 'prayer':
        return (
          <Prayer 
            groupId={id} 
            searchQuery={searchQuery}
            isAdmin={isAdmin} 
            highlightedQuery={searchQuery}
            onDataLoad={(data) => updateTabData('prayer', data)}
            filteredData={displayData.prayer}
          />
        );
      case 'memberships':
        return (
          <MembershipsList 
            groupId={id} 
            searchQuery={searchQuery}
            isAdmin={isAdmin}
            highlightedQuery={searchQuery}
            onDataLoad={(data) => updateTabData('memberships', data)}
            filteredData={displayData.memberships}
          />
        );
      default:
        return (
          <UpcomingList 
            groupId={id} 
            searchQuery={searchQuery} 
            isAdmin={isAdmin}
            highlightedQuery={searchQuery}
            onDataLoad={(data) => updateTabData('upcoming', data)}
            filteredData={displayData.upcoming}
          />
        );
    }
  };

  // If unauthorized admin attempt is detected, redirect to dashboard
  // Note: We've removed unauthorized redirect for public access
  // if (unauthorizedAttempt && currentUser?.role === 'admin') {
  //   return <Navigate to="/dashboard" replace />;
  // }

  if (loading || groupContextLoading) {
    return (
      <div className="group-page-loading">
        <div className="loading-spinner"></div>
        <p>Loading group details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="group-page-error">
        <p>{error}</p>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="group-page-error">
        <p>Group not found.</p>
      </div>
    );
  }

  const groupContent = (
    <div className="group-page-container">
      {/* Group Header with Logo and Name */}
      <GroupHeader group={group} />
      
      {/* Search Bar */}
      <div className={`group-search-container ${!searchBarVisible ? 'hidden' : ''}`}>
        <div className="search-bar-container">
          <div className="search-bar">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearch}
            />
            {searchQuery && (
              <button 
                className="clear-search-button"
                onClick={() => setSearchQuery('')}
                tabIndex={0}
                aria-label="Clear search"
                type="button"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Tabs Navigation */}
      <GroupTabs 
        activeTab={activeTab} 
        onTabChange={(tab) => handleTabChange(tab)} 
        searchBarVisible={searchBarVisible}
        isAdmin={isAdmin}
      />
      
      {/* Tab Content with swipe functionality */}
      <div 
        ref={contentRef}
        className="group-content-container" 
        {...contentSwipeHandlers}
      >
        {renderTabContent()}
      </div>
      
      {/* Swipe direction indicators */}
      <div className={`swipe-indicator left ${swipeDirection === 'left' ? 'visible' : ''}`}>
        <FontAwesomeIcon icon={faChevronRight} />
      </div>
      <div className={`swipe-indicator right ${swipeDirection === 'right' ? 'visible' : ''}`}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </div>
      
      {/* Floating Create Button (only visible to admins) */}
      {isAdmin && <CreateButton groupId={id} activeTab={activeTab} />}
      
      {/* Floating CharityBox (only visible to members) */}
      {isMember && <CharityBox groupId={id} groupName={group?.name} isMember={isMember} />}
    </div>
  );

  // Wrap admin view with AdminLayout, regular members see normal view
  return isAdmin ? (
    <AdminLayout>{groupContent}</AdminLayout>
  ) : (
    groupContent
  );
};

export default GroupPage;